import { memo, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import MediaLightbox from '../MediaLightbox';
import GenerationStrip from './GenerationStrip';
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  IconButton,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Delete, Videocam, PlayArrow } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { HANDLES, type VideoNodeData, type GenerateNodeData, type GenerationEntry, useCanvasStore } from '../useCanvasStore';
import { studioService, type ImageMode } from '../../../../services/studio.service';

import { useStudioStore } from '../../stores/studio.store';
import { getAssetSrc } from '../../utils/assetUrl';
import { useNodeTextField } from './useNodeTextField';
import SaveToLibraryButton from '../../components/SaveToLibraryButton';

function VideoNode({ id, data }: NodeProps & { data: VideoNodeData }) {
  const updateNodeData = useCanvasStore((s) => s.updateNodeData);
  const removeNode = useCanvasStore((s) => s.removeNode);
  const edges = useCanvasStore((s) => s.edges); // subscribe to edge changes
  const allNodes = useCanvasStore((s) => s.nodes); // subscribe to node changes
  const addAsset = useStudioStore((s) => s.addAsset);
  const setAssetServerId = useStudioStore((s) => s.setAssetServerId);
  const projectId = useStudioStore((s) => s.projectId);
  const { enqueueSnackbar } = useSnackbar();
  const syncPrompt = useMemo(() => (v: string) => updateNodeData(id, { prompt: v }), [id, updateNodeData]);
  const promptField = useNodeTextField(data.prompt, syncPrompt);
  const pollRef = useRef<ReturnType<typeof setInterval>>();
  const [lightbox, setLightbox] = useState(false);

  // Reactive: re-compute when edges/nodes change
  const myEdges = edges.filter((e) => e.target === id);
  const startFrameNode = (() => {
    const e = myEdges.find((e) => e.targetHandle === HANDLES.startFrame);
    return e ? allNodes.find((n) => n.id === e.source) : undefined;
  })();
  const endFrameNode = (() => {
    const e = myEdges.find((e) => e.targetHandle === HANDLES.endFrame);
    return e ? allNodes.find((n) => n.id === e.source) : undefined;
  })();
  const styleRefNodes = myEdges
    .filter((e) => e.targetHandle === HANDLES.styleRef)
    .map((e) => allNodes.find((n) => n.id === e.source))
    .filter(Boolean);
  const videoInNode = (() => {
    const e = myEdges.find((e) => e.targetHandle === HANDLES.videoIn);
    return e ? allNodes.find((n) => n.id === e.source) : undefined;
  })();

  const startImage = (startFrameNode?.data as GenerateNodeData)?.resultImage;
  const endImage = (endFrameNode?.data as GenerateNodeData)?.resultImage;
  const sourceVideo = (videoInNode?.data as VideoNodeData)?.resultVideo;

  // Auto-match aspect ratio from connected image or video
  useEffect(() => {
    const detectAspectRatio = (width: number, height: number) => {
      const ratio = width / height;
      // Portrait if taller than wide (ratio < 0.8)
      return ratio < 0.8 ? '9:16' : '16:9';
    };

    if (startImage) {
      const img = new Image();
      img.onload = () => {
        const ar = detectAspectRatio(img.naturalWidth, img.naturalHeight);
        if (ar !== data.aspectRatio) updateNodeData(id, { aspectRatio: ar });
      };
      img.src = getAssetSrc(startImage, 'image/png');
    } else if (sourceVideo) {
      const vid = document.createElement('video');
      vid.onloadedmetadata = () => {
        const ar = detectAspectRatio(vid.videoWidth, vid.videoHeight);
        if (ar !== data.aspectRatio) updateNodeData(id, { aspectRatio: ar });
      };
      vid.src = sourceVideo;
    }
  }, [startImage, sourceVideo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for video status with exponential backoff
  useEffect(() => {
    if (data.status !== 'processing' || !data.jobId) return;
    let pollCount = 0;
    let assetWaits = 0;

    const poll = async () => {
      try {
        const status = await studioService.checkVideoStatus(data.jobId!);
        // The server saves a project's video just after marking it completed; recording it before the asset id
        // arrives would make the next project save upload a second copy. Without one after a few polls, record it anyway.
        if (status.status === 'completed' && status.videoUrl && !status.assetId && projectId && assetWaits < 5) {
          assetWaits++;
          pollRef.current = setTimeout(poll, 3000);
          return;
        }
        if (status.status === 'completed' && status.videoUrl) {
          const newGen: GenerationEntry = {
            video: status.videoUrl,
            mimeType: 'video/mp4',
            prompt: data.prompt,
            timestamp: Date.now(),
          };
          const prevGens = data.generations || [];
          const newGens = [...prevGens, newGen];
          const newIdx = newGens.length - 1;
          const storeAssetId = addAsset({
            type: 'video',
            data: status.videoUrl,
            mimeType: 'video/mp4',
            prompt: data.prompt,
            tool: 'video',
            ...(status.assetId && { serverId: status.assetId }),
          });
          updateNodeData(id, {
            status: 'completed',
            resultVideo: status.videoUrl,
            generations: newGens,
            selectedGeneration: newIdx,
            storeAssetId,
            ...(status.assetId && { assetId: status.assetId }),
          });
          return;
        }
        if (status.status === 'failed') {
          updateNodeData(id, { status: 'failed', error: status.errorMessage || 'Video generation failed' });
          return;
        }
        if (status.status === 'timed_out') {
          updateNodeData(id, { status: 'failed', error: 'Video generation timed out' });
          return;
        }
        // Schedule next poll with backoff: 5s, 7.5s, 11.25s, ... capped at 30s
        pollCount++;
        const nextDelay = Math.min(5000 * Math.pow(1.5, pollCount), 30000);
        pollRef.current = setTimeout(poll, nextDelay);
      } catch {
        pollCount++;
        const nextDelay = Math.min(5000 * Math.pow(1.5, pollCount), 30000);
        pollRef.current = setTimeout(poll, nextDelay);
      }
    };

    // First poll after 5s
    pollRef.current = setTimeout(poll, 5000);
    return () => clearTimeout(pollRef.current);
  }, [data.status, data.jobId, id, data.prompt, projectId, updateNodeData, addAsset]);

  // Backfill: a completed node without an assetId keeps checking for a while (the server's save may land
  // after the node gave up waiting), and links the store asset too so a project save doesn't upload it again.
  useEffect(() => {
    // Without a project nothing is saved server-side, so there is no id to wait for.
    if (data.status !== 'completed' || !data.jobId || data.assetId || !projectId) return;
    let cancelled = false;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const retry = () => { if (!cancelled && ++tries < 6) timer = setTimeout(check, 10000); };
    const check = () => {
      studioService.checkVideoStatus(data.jobId!).then((s) => {
        if (cancelled) return;
        if (!s.assetId) return retry();
        updateNodeData(id, { assetId: s.assetId });
        if (data.storeAssetId) setAssetServerId(data.storeAssetId, s.assetId);
      }).catch(retry);
    };
    check();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [data.status, data.jobId, data.assetId, data.storeAssetId, projectId, id, updateNodeData, setAssetServerId]);

  const handleSelectGeneration = useCallback((idx: number) => {
    const gens = data.generations || [];
    const gen = gens[idx];
    if (!gen) return;
    updateNodeData(id, {
      selectedGeneration: idx,
      resultVideo: gen.video,
      ...(gen.prompt !== undefined && { prompt: gen.prompt }),
    });
  }, [id, data.generations, updateNodeData]);

  const handleGenerate = useCallback(async () => {
    if (!data.prompt.trim()) return;
    updateNodeData(id, { status: 'generating' });

    try {
      // Determine image mode based on connections
      let imageMode: ImageMode = 'startFrame';
      const images: File[] = [];

      const toFile = async (base64: string, name: string): Promise<File> => {
        const resp = await fetch(getAssetSrc(base64, 'image/png'));
        const blob = await resp.blob();
        return new File([blob], name, { type: 'image/png' });
      };

      if (startImage && endImage) {
        imageMode = 'startEnd';
        images.push(await toFile(startImage, 'start.png'));
        images.push(await toFile(endImage, 'end.png'));
      } else if (startImage && styleRefNodes.length > 0) {
        // Start frame + reference images — use startFrame mode with ref as additional images
        // The backend will send start as image + refs as referenceImages
        imageMode = 'startFrame';
        images.push(await toFile(startImage, 'start.png'));
        for (const ref of styleRefNodes) {
          const img = (ref!.data as GenerateNodeData).resultImage;
          if (img) images.push(await toFile(img, 'ref.png'));
        }
      } else if (startImage) {
        imageMode = 'startFrame';
        images.push(await toFile(startImage, 'start.png'));
      } else if (endImage) {
        imageMode = 'endFrame';
        images.push(await toFile(endImage, 'end.png'));
      } else if (styleRefNodes.length > 0) {
        // Veo 3.1 supports ASSET references (subject/scene), not STYLE
        imageMode = 'asset';
        for (const ref of styleRefNodes) {
          const img = (ref!.data as GenerateNodeData).resultImage;
          if (img) images.push(await toFile(img, 'ref.png'));
        }
      }

      let res;
      const videoOpts = {
        aspectRatio: data.aspectRatio as '16:9' | '9:16',
        duration: data.duration,
        audioGeneration: true,
        numberOfVideos: 1 as const,
        enhancePrompt: data.enhancePrompt ?? true,
        quality: data.quality || 'fast',
      };
      const provider = (data.provider as 'veo' | 'sora') || 'veo';

      if (sourceVideo) {
        // Video-to-video extend
        if (provider === 'sora') {
          // Sora extends by sourceJobId — find the source video node's jobId
          const sourceJobId = (videoInNode?.data as VideoNodeData | undefined)?.jobId;
          if (!sourceJobId) {
            throw new Error('Sora extension requires the source video to be a completed Sora job');
          }
          res = await studioService.extendVideo(
            null,
            data.prompt,
            videoOpts,
            projectId || undefined,
            'sora',
            sourceJobId,
          );
        } else {
          const videoResp = await fetch(sourceVideo);
          const videoBlob = await videoResp.blob();
          const videoFile = new File([videoBlob], 'source.mp4', { type: 'video/mp4' });
          res = await studioService.extendVideo(
            videoFile,
            data.prompt,
            videoOpts,
            projectId || undefined,
            'veo',
          );
        }
      } else if (images.length > 0) {
        res = await studioService.videoFromImages(
          images,
          data.prompt,
          videoOpts,
          imageMode,
          projectId || undefined,
          provider,
        );
      } else {
        res = await studioService.generateVideo(
          data.prompt,
          videoOpts,
          projectId || undefined,
          provider,
        );
      }

      updateNodeData(id, {
        status: 'processing',
        jobId: res.jobId,
        operationId: res.operationId,
      });
      enqueueSnackbar('Video generating — this takes 1-3 minutes', { variant: 'info' });
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
      updateNodeData(id, { status: 'failed', error: (err as Error).message });
    }
  }, [id, data.prompt, data.aspectRatio, data.duration, startImage, endImage, styleRefNodes, projectId, updateNodeData, enqueueSnackbar, addAsset]);

  const statusColor =
    data.status === 'completed' ? 'success' :
    data.status === 'failed' ? 'error' :
    data.status === 'processing' || data.status === 'generating' ? 'warning' : 'default';

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        width: 300,
        overflow: 'hidden',
      }}
    >
      {/* Input handles (left side) */}
      <Handle
        type="target"
        position={Position.Left}
        id={HANDLES.startFrame}
        style={{ background: '#66bb6a', width: 10, height: 10, top: '30%' }}
        title="Start Frame"
      />
      <Handle
        type="target"
        position={Position.Left}
        id={HANDLES.endFrame}
        style={{ background: '#ef5350', width: 10, height: 10, top: '50%' }}
        title="End Frame"
      />
      <Handle
        type="target"
        position={Position.Left}
        id={HANDLES.styleRef}
        style={{ background: '#ab47bc', width: 10, height: 10, top: '60%' }}
        title="Reference Image (subject/scene reference)"
      />
      <Handle
        type="target"
        position={Position.Left}
        id={HANDLES.videoIn}
        style={{ background: '#1565c0', width: 10, height: 10, top: '80%' }}
        title="Video In (extend/continue)"
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          bgcolor: '#1565c0',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Videocam sx={{ fontSize: 16 }} />
          <Typography variant="caption" fontWeight={600}>
            Generate Video
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => removeNode(id)} sx={{ color: 'inherit' }}>
          <Delete sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Input labels */}
      <Box sx={{ px: 1.5, pt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={startImage ? '✓ Start Frame' : '○ Start Frame'}
            color={startImage ? 'success' : 'default'}
            variant={startImage ? 'filled' : 'outlined'}
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
          <Chip
            size="small"
            label={endImage ? '✓ End Frame' : '○ End Frame'}
            color={endImage ? 'error' : 'default'}
            variant={endImage ? 'filled' : 'outlined'}
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
          <Chip
            size="small"
            label={styleRefNodes.length ? '✓ Ref Image' : '○ Ref Image'}
            color={styleRefNodes.length ? 'secondary' : 'default'}
            variant={styleRefNodes.length ? 'filled' : 'outlined'}
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
          <Chip
            size="small"
            label={sourceVideo ? '✓ Video In' : '○ Video In'}
            color={sourceVideo ? 'info' : 'default'}
            variant={sourceVideo ? 'filled' : 'outlined'}
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          size="small"
          multiline
          minRows={2}
          maxRows={4}
          placeholder="Describe the video..."
          value={promptField.value}
          onChange={promptField.onChange}
          onFocus={promptField.onFocus}
          onBlur={promptField.onBlur}
          fullWidth
          className="nodrag nowheel"
        />

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Select
            size="small"
            value={data.provider || 'veo'}
            onChange={(e) => updateNodeData(id, { provider: e.target.value as 'veo' | 'sora' })}
            className="nodrag"
            sx={{ fontSize: '0.75rem', flex: 1 }}
          >
            <MenuItem value="veo" sx={{ fontSize: '0.75rem' }}>Veo 3.1</MenuItem>
            <MenuItem value="sora" sx={{ fontSize: '0.75rem' }}>Sora 2</MenuItem>
          </Select>
          {(data.provider || 'veo') === 'veo' && (
            <Select
              size="small"
              value={data.quality || 'fast'}
              onChange={(e) => updateNodeData(id, { quality: e.target.value as 'lite' | 'fast' | 'standard' })}
              className="nodrag"
              sx={{ fontSize: '0.75rem', flex: 1 }}
            >
              <MenuItem value="lite" sx={{ fontSize: '0.75rem' }}>Lite</MenuItem>
              <MenuItem value="fast" sx={{ fontSize: '0.75rem' }}>Fast</MenuItem>
              <MenuItem value="standard" sx={{ fontSize: '0.75rem' }}>Standard</MenuItem>
            </Select>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Select
            size="small"
            value={data.aspectRatio}
            onChange={(e) => updateNodeData(id, { aspectRatio: e.target.value as VideoNodeData['aspectRatio'] })}
            className="nodrag"
            sx={{ flex: 1 }}
          >
            <MenuItem value="16:9">16:9</MenuItem>
            <MenuItem value="9:16">9:16</MenuItem>
          </Select>
          <Select
            size="small"
            value={data.duration}
            onChange={(e) => updateNodeData(id, { duration: e.target.value as VideoNodeData['duration'] })}
            className="nodrag"
            sx={{ flex: 1 }}
          >
            <MenuItem value="4s">4s</MenuItem>
            <MenuItem value="6s">6s</MenuItem>
            <MenuItem value="8s">8s</MenuItem>
          </Select>
        </Box>

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={data.enhancePrompt ?? true}
              onChange={(e) => updateNodeData(id, { enhancePrompt: e.target.checked })}
              className="nodrag"
            />
          }
          label={<Typography variant="caption">Enhance prompt</Typography>}
          sx={{ ml: 0 }}
        />

        <Button
          variant="contained"
          size="small"
          onClick={handleGenerate}
          disabled={data.status === 'generating' || data.status === 'processing' || !data.prompt.trim()}
          startIcon={
            data.status === 'generating' || data.status === 'processing' ? (
              <CircularProgress size={14} />
            ) : (
              <PlayArrow sx={{ fontSize: 14 }} />
            )
          }
          fullWidth
        >
          {data.status === 'generating'
            ? 'Starting…'
            : data.status === 'processing'
            ? 'Processing…'
            : sourceVideo
            ? 'Extend Video'
            : 'Generate Video'}
        </Button>

        {data.status && data.status !== 'idle' && !data.resultVideo && (
          <Chip
            size="small"
            label={data.status === 'completed' ? 'Done' : data.status === 'failed' ? data.error || 'Failed' : 'Processing…'}
            color={statusColor as 'success' | 'error' | 'warning' | 'default'}
            sx={{ fontSize: '0.7rem' }}
          />
        )}

        {/* Result preview */}
        {data.resultVideo && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: data.aspectRatio.replace(':', '/'),
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <video
              src={data.resultVideo}
              style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
              onClick={() => setLightbox(true)}
              muted
              loop
              autoPlay
              playsInline
            />
            <SaveToLibraryButton assetId={data.assetId as string | undefined} defaultName={data.prompt} />
          </Box>
        )}
        {data.resultVideo && (
          <MediaLightbox
            open={lightbox}
            onClose={() => setLightbox(false)}
            src={data.resultVideo}
            type="video"
          />
        )}

        {/* Generation history strip */}
        {data.generations && data.generations.length > 1 && (
          <GenerationStrip
            generations={data.generations}
            selectedIndex={data.selectedGeneration ?? data.generations.length - 1}
            onSelect={handleSelectGeneration}
            type="video"
          />
        )}
      </Box>

      {/* Output handle */}
      {data.resultVideo && (
        <Handle
          type="source"
          position={Position.Right}
          id={HANDLES.videoOut}
          style={{ background: '#42a5f5', width: 10, height: 10 }}
        />
      )}
    </Box>
  );
}

export default memo(VideoNode);
